import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import { mergeWithDeFallback } from "@/i18n/clientDeFallback";
import {
  baseLanguageFor,
  coerceLanguage,
  dirFor,
  isSupportedLanguage,
  type SupportedLanguage,
} from "@/lib/i18n/languages";
import { rendersInBaseLanguage } from "@/lib/i18n/tenant-languages";
import { resolveTranslation, type TranslationDict } from "@/lib/i18n/resolve";
import { isMissingColumn } from "@/hooks/v2/useContractFlowReady";

/**
 * COL-478: six languages. Before this the union `"en" | "de"` was re-spelled
 * across the app; import this (or SupportedLanguage) instead.
 */
export type Language = SupportedLanguage;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  /** Lookup translation by key; optional `fallback` used when DB has no entry (avoids raw keys in UI). */
  t: (key: string, fallback?: string) => string;
  /**
   * `t()` for the tenant dashboard dictionaries (COL-479), whose literals
   * ship with the code.
   *
   * `literalLanguage` says which language the literal is actually written in.
   * When that IS the reader's language, it outranks a row in a different one:
   * a German tenant whose `de` column is empty gets the German literal, not
   * the English row. When it is NOT — the dictionaries hand English to a
   * French reader, since that copy is not translated yet — the literal stays
   * below the database tiers, so an admin's correction to the English row
   * still reaches them.
   */
  tDict: (
    key: string,
    literal: string,
    literalLanguage: SupportedLanguage
  ) => string;
  isLoading: boolean;
  /** False only until the first payload exists (persisted cache or network); stays true during background refetch. */
  isTranslationsReady: boolean;
  /**
   * Legacy Storyblok sync trigger. Kept on the context for back-compat
   * with callers that haven't migrated yet, but is now a no-op shim:
   * the authoritative source is the public.translations table edited
   * from V2 admin (/admin/translations). Calling this just invalidates
   * the React Query cache so the next read pulls fresh rows.
   *
   * @deprecated Storyblok is no longer the source of truth. Edit
   * translations in V2 admin instead. This shim will be removed once
   * the last caller is migrated.
   */
  syncTranslations: (languageFilter?: Language) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// Bumped from v1 to v2 when we moved from the Storyblok-backed
// translation_cache (single jsonb blob per language) to the per-key
// public.translations table. The shape on the wire is the same
// client-side dict, but old v1 payloads under the previous key would
// still resolve — the bump forces a clean refetch on first load
// after deploy.
const TRANSLATION_LS_PREFIX = "coliviq.translations.v2";

/**
 * What one language's fetch produces. `translations` is the active-language
 * dict every consumer has always seen. For the four non-base languages
 * (COL-478) the English and German dicts ride along so `t()` can fall back
 * active → en → de without a second network round trip; for en/de they are
 * absent and the existing de → en fallback reads the English query from the
 * react-query cache as before.
 */
type TranslationPayload = {
  translations: TranslationDict;
  en?: TranslationDict;
  de?: TranslationDict;
  /**
   * False when this payload is known to be MISSING rows -- a failed first
   * page, or a page that dropped out of the parallel fetch. React Query's
   * own `dataUpdatedAt` is stamped the moment `fetchTranslations` resolves,
   * REGARDLESS of whether the payload is complete, so `savedAt: 0` on the
   * localStorage copy only protects a fresh page load; it does nothing for
   * a query that is already mounted and refetches into an incomplete result
   * (Codex, 14 Oct). `staleTime` below reads this flag directly off the
   * live query's own data to close that gap.
   */
  complete: boolean;
};

/**
 * The en + de tiers every language resolves through, as their own payload.
 *
 * `resolveTranslation` has always taken `active` / `en` / `de` separately —
 * this is just the fetch finally matching that shape, so the ~8,800 rows
 * behind the two tiers are downloaded once per device instead of once per
 * language. See `fetchTranslations`.
 */
export type BaseDictionaries = {
  en: TranslationDict;
  de: TranslationDict;
  complete: boolean;
  /**
   * True when this copy came off localStorage rather than the network —
   * a failed request, or a refresh that lost a page and fell back.
   *
   * `complete` cannot carry that: a stored copy legitimately HAS every key,
   * so calling it incomplete would be false and would make every offline
   * load refetch hard. What is untrue about it is its AGE, and react-query
   * stamps `dataUpdatedAt: now` on whatever a queryFn resolves with — so
   * without this flag a days-old dictionary reads as freshly verified.
   */
  fromStorage?: boolean;
};

function isBaseLanguage(language: Language): language is "en" | "de" {
  return language === "en" || language === "de";
}

function translationStorageKey(language: Language): string {
  return `${TRANSLATION_LS_PREFIX}.${language}`;
}

/**
 * The en + de tiers, stored ONCE rather than inside each language's entry.
 *
 * They are the same bytes whatever the active language is, so the old
 * per-language copies meant six near-identical ~1 MB payloads competing for a
 * ~5 MB localStorage budget — the quota catch below was swallowing real
 * writes. `__base` cannot collide with a language code.
 */
const BASE_STORAGE_KEY = `${TRANSLATION_LS_PREFIX}.__base`;

/** Cache entry for the shared en/de tiers. Under the `["translations"]`
 *  prefix so `syncTranslations()`'s invalidation sweeps it in too. */
const TRANSLATION_BASE_QUERY_KEY = ["translations", "__base"] as const;

const TRANSLATIONS_STALE_MS = 1000 * 60 * 5;
const TRANSLATIONS_GC_MS = 1000 * 60 * 30;

function isDict(value: unknown): value is TranslationDict {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// mergeWithDeFallback only acts on "de"; everything else passes through.
function deFallbackKey(language: Language): "de" | "en" {
  return language === "de" ? "de" : "en";
}

/**
 * When the persisted payload for `language` was written, as an epoch ms, or 0
 * when there is none / it predates the stamp.
 *
 * Fed to react-query as `initialDataUpdatedAt`. Without it, `initialData` is
 * treated as fresh AS OF NOW, so a non-zero staleTime would serve a payload
 * written days ago and skip the refetch entirely -- trading the slow load for
 * stale copy. With it, a payload older than staleTime still paints instantly
 * and is refreshed in the background, which is the whole point of persisting
 * it. Entries written before this stamp existed return 0, i.e. always stale.
 */
function readPersistedSavedAt(language: Language): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = localStorage.getItem(translationStorageKey(language));
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { savedAt?: unknown };
    return typeof parsed?.savedAt === "number" ? parsed.savedAt : 0;
  } catch {
    return 0;
  }
}

function readPersistedBase(): BaseDictionaries | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = localStorage.getItem(BASE_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<BaseDictionaries>;
    if (!isDict(parsed?.en) || !isDict(parsed?.de)) return undefined;
    return {
      en: parsed.en,
      de: parsed.de,
      complete: typeof parsed.complete === "boolean" ? parsed.complete : false,
    };
  } catch {
    return undefined;
  }
}

function persistBase(base: BaseDictionaries): void {
  if (typeof window === "undefined") return;
  try {
    // A fetch that lost a page must not demote a copy that has every key.
    // The incomplete one is still worth keeping when it is all we have —
    // painting yesterday's copy beats painting raw keys — but overwriting a
    // whole dictionary with a holed one is a straight downgrade, and these
    // tiers are what every language falls back through.
    if (!base.complete && readPersistedBase()?.complete) return;
    localStorage.setItem(
      BASE_STORAGE_KEY,
      // Same rule as the per-language entry: an incomplete payload is worth
      // keeping to paint with, but must never read as fresh.
      JSON.stringify({ ...base, savedAt: base.complete ? Date.now() : 0 })
    );
  } catch {
    // ignore quota / private mode
  }
}

function readPersistedTranslations(
  language: Language
): TranslationPayload | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = localStorage.getItem(translationStorageKey(language));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<TranslationPayload>;
    if (!parsed || !isDict(parsed.translations)) {
      return undefined;
    }
    /*
      Default to INCOMPLETE for an entry written before this field existed.
      `initialDataUpdatedAt` already reads such an entry's missing `savedAt`
      as 0 (always stale), which triggers an immediate refetch either way --
      this only decides what `complete` reads as during the brief window
      before that refetch resolves, and "unknown" should not paint as "safe".
    */
    let complete =
      typeof parsed.complete === "boolean" ? parsed.complete : false;

    // The fallback tiers now live in their own entry. `parsed.en` / `parsed.de`
    // are read as a fallback for entries written before that split, so an
    // upgrading browser paints from what it already has instead of blanking.
    let en: TranslationDict | undefined;
    let de: TranslationDict | undefined;
    if (!isBaseLanguage(language)) {
      const base = readPersistedBase();
      en = base?.en ?? (isDict(parsed.en) ? parsed.en : undefined);
      de = base?.de ?? (isDict(parsed.de) ? parsed.de : undefined);
      // Without the tiers, every key this language has not translated resolves
      // to the caller's fallback — a half-English page. Report that as
      // incomplete so the query refetches at once rather than holding it for
      // five minutes.
      if (!en || !de) complete = false;
    }

    return {
      translations: mergeWithDeFallback(
        deFallbackKey(language),
        parsed.translations
      ),
      en,
      de,
      complete,
    };
  } catch {
    return undefined;
  }
}

function persistTranslations(
  language: Language,
  payload: TranslationPayload
): void {
  /*
    `payload.complete` is the single source of truth for whether this is the
    WHOLE dictionary -- a failed first page, or a page that dropped out of
    the parallel fetch, sets it false. An incomplete payload is still worth
    persisting, because painting yesterday's copy beats painting raw keys.
    What it must NOT do is claim to be fresh: stamped with `now`, a non-zero
    staleTime would hold it authoritative for minutes, and every `t()`
    without a fallback renders its own key (`nav.dashboard` in the header,
    seen 14 Oct). Stamping it stale paints it instantly and refetches at
    once.

    This only protects a FRESH page load reading the localStorage copy back
    via `initialDataUpdatedAt` -- it does nothing for a query that is already
    mounted and refetches into an incomplete result, which is why `staleTime`
    below also reads `payload.complete` directly off the live query's data.
  */
  const complete = payload.complete;
  if (typeof window === "undefined") return;
  try {
    // `en` / `de` are deliberately dropped: they are the shared tiers and
    // `persistBase` owns them. Storing them here too is what made each
    // language's entry ~1 MB.
    localStorage.setItem(
      translationStorageKey(language),
      JSON.stringify({
        translations: payload.translations,
        complete,
        savedAt: complete ? Date.now() : 0,
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

type TranslationRow = { key: string } & Partial<
  Record<SupportedLanguage, string | null>
>;

function projectColumn(
  rows: TranslationRow[],
  column: SupportedLanguage
): TranslationDict {
  const dict: TranslationDict = {};
  for (const row of rows) {
    const value = row[column];
    if (typeof value === "string" && value.length > 0) {
      dict[row.key] = value;
    }
  }
  return dict;
}

/**
 * Page through `translations` with a stable order.
 *
 * PostgREST returns at most 1000 rows per request — the project's `max-rows`,
 * which a bigger `limit` does not raise — and in arbitrary physical order
 * when unordered, so a plain select silently drops keys once the table passes
 * 1000 rows, and the missing ones (newer keys land last) render their English
 * fallback in German.
 *
 * Pages after the first go out in PARALLEL. The table is ~8,800 keys, so a
 * serial loop is nine round trips stacked end to end, and until it finishes
 * the provider below renders a full-screen spinner instead of the app. Ask
 * for the count with the first page, then request whatever is left at once.
 *
 * Returns null when the FIRST page fails: nothing usable arrived, and the
 * caller decides what to show instead. A later page failing keeps what did
 * arrive — partial beats blank — and reports `complete: false`, which is what
 * stops a partial dictionary being cached or persisted as authoritative.
 *
 * `onlyWhereSet` narrows the scan to rows that actually carry that column.
 */
async function fetchRowsPaged(
  columns: string,
  onlyWhereSet?: SupportedLanguage
): Promise<{ rows: TranslationRow[]; complete: boolean } | null> {
  const PAGE = 1000;
  const page = (from: number, withCount: boolean) => {
    const table = (supabase as any).from("translations");
    let query = withCount
      ? table.select(columns, { count: "exact" })
      : table.select(columns);
    if (onlyWhereSet) query = query.not(onlyWhereSet, "is", null);
    return query.order("key", { ascending: true }).range(from, from + PAGE - 1);
  };

  const first = await page(0, true);
  if (first.error) {
    console.warn("Could not load translations:", first.error);
    return null;
  }

  const rows: TranslationRow[] = [...((first.data ?? []) as TranslationRow[])];
  // `count` can come back null when the server declines to count; fall back
  // to "there may be more if this page was full", which is the old rule.
  const total: number | null = first.count ?? null;
  /*
    Declared before EITHER branch that can fail a page, not just the parallel
    one below. The serial walk used to `break` silently on a page error with
    no record of it -- the resulting partial dictionary was then persisted and
    treated as 100% complete (Codex, 14 Oct).
  */
  let complete = true;

  if (total === null && rows.length === PAGE) {
    // Unknown total: degrade to the serial walk rather than guess a size.
    for (let from = PAGE; ; from += PAGE) {
      const { data, error } = await page(from, false);
      if (error) {
        complete = false;
        break;
      }
      if (!data || data.length === 0) break;
      rows.push(...(data as TranslationRow[]));
      if (data.length < PAGE) break;
    }
    return { rows, complete };
  }

  const remaining: number[] = [];
  if (total !== null) {
    for (let from = PAGE; from < total; from += PAGE) remaining.push(from);
  }
  if (remaining.length > 0) {
    const pages = await Promise.all(remaining.map((from) => page(from, false)));
    for (const result of pages) {
      if (result.error || !result.data) {
        complete = false;
        continue;
      }
      rows.push(...(result.data as TranslationRow[]));
    }
  }
  return { rows, complete };
}

/**
 * The en + de tiers, for every key — one cache entry shared by all six
 * languages, because they are the same bytes whichever one is active.
 *
 * Never resolves with LESS than the device already had. A failed request
 * falls back to the stored copy, and so does a fetch that lost a page: the
 * caller would otherwise replace a whole dictionary with a holed one and then
 * build every language's payload from the holes.
 *
 * Whatever it hands back in those cases is flagged `fromStorage`, because it
 * did NOT come off the network. The caller must not read it as a freshly
 * verified copy — see `loadSharedBase`.
 *
 * NEVER REJECTS — the await is inside the try, so a throwing client is
 * caught here and reported by resolving, not by rejecting. `loadSharedBase`
 * and the `staleTime` rule both depend on a failure arriving as data with
 * `complete: false` or `fromStorage: true`; a rejection would bypass both.
 */
export async function fetchBaseDictionaries(): Promise<BaseDictionaries> {
  const stored = (): BaseDictionaries | undefined => {
    const kept = readPersistedBase();
    return kept ? { ...kept, fromStorage: true } : undefined;
  };
  const storedOrEmpty = (): BaseDictionaries =>
    stored() ?? { en: {}, de: {}, complete: false };
  try {
    const paged = await fetchRowsPaged("key, en, de");
    if (!paged) return storedOrEmpty();
    const base: BaseDictionaries = {
      en: projectColumn(paged.rows, "en"),
      de: projectColumn(paged.rows, "de"),
      complete: paged.complete,
    };
    // A refresh that dropped a page must not demote a whole copy — not in
    // localStorage (`persistBase` guards that) and not in the query cache
    // either, which is what the caller actually reads from.
    if (!base.complete) {
      const kept = stored();
      if (kept?.complete) return kept;
    }
    persistBase(base);
    return base;
  } catch (error) {
    console.error("Error fetching base translations:", error);
    return storedOrEmpty();
  }
}

/**
 * Only the rows this language actually translates.
 *
 * `fr IS NOT NULL` is 1,069 of 8,810 rows on live. The rest carry no French
 * at all, so the resolver reads straight past them to the en/de tiers the
 * base already holds — downloading them again per language bought nothing.
 */
async function fetchLanguageOverlay(
  language: Exclude<Language, "en" | "de">
): Promise<{ dict: TranslationDict; complete: boolean }> {
  const paged = await fetchRowsPaged(`key, ${language}`, language);
  if (!paged) return { dict: {}, complete: false };
  return {
    dict: projectColumn(paged.rows, language),
    complete: paged.complete,
  };
}

/**
 * The dictionary for one language, in the shape the provider's query wants —
 * unchanged for consumers, so no callsite moves.
 *
 * Split in two because the halves have different lifetimes: `en`/`de` are the
 * tiers EVERY language resolves through, fetched once per device, while the
 * active language contributes only the rows it translates. A de→fr switch
 * therefore costs one small overlay instead of the whole table again, and
 * de→en costs no request at all.
 *
 * `loadBase` is injected so the provider can route it through react-query and
 * share one in-flight request across languages; it defaults to the bare fetch
 * for callers and tests that have no query client. Both halves run
 * concurrently, so a cold load of a new language is still one round of pages
 * rather than base-then-overlay.
 */
export async function fetchTranslations(
  language: Language,
  loadBase: () => Promise<BaseDictionaries> = fetchBaseDictionaries
): Promise<TranslationPayload> {
  const fallbackOnly = (): TranslationPayload => ({
    translations: mergeWithDeFallback(deFallbackKey(language), {}),
    complete: false,
  });
  try {
    if (isBaseLanguage(language)) {
      const base = await loadBase();
      const payload: TranslationPayload = {
        translations: mergeWithDeFallback(
          deFallbackKey(language),
          base[language]
        ),
        complete: base.complete,
      };
      // Nothing reached the network, so re-stamping this entry would date a
      // dictionary we never verified — and `savedAt` is what the next load
      // reads to decide whether to refetch at all. Leaving the old stamp
      // keeps that load honest. See `BaseDictionaries.fromStorage`.
      if (!base.fromStorage) persistTranslations(language, payload);
      return payload;
    }

    const [base, overlay] = await Promise.all([
      loadBase(),
      fetchLanguageOverlay(language),
    ]);
    const payload: TranslationPayload = {
      translations: mergeWithDeFallback(deFallbackKey(language), overlay.dict),
      en: base.en,
      de: base.de,
      complete: base.complete && overlay.complete,
    };
    if (!base.fromStorage) persistTranslations(language, payload);
    return payload;
  } catch (error) {
    console.error("Error fetching translations:", error);
    return fallbackOnly();
  }
}

/**
 * The shared en/de tiers, as the language query's `loadBase`.
 *
 * The question is only ever "may the cached copy answer this?", and the three
 * ways it cannot are worth keeping distinct, because two of them must be
 * AWAITED and the third must not:
 *
 *  - PARTIAL. `fetchBaseDictionaries` never throws, so a dropped page or a
 *    dead network still resolves — with `complete: false`. Serving that for
 *    the next five minutes would leave every language resolving through
 *    holes, which is English fallback or raw keys on screen. Awaited; it is
 *    the same rule the language query's own `staleTime` applies to itself,
 *    and the shared entry needs it too or the guarantee has a hole exactly
 *    where the tiers live.
 *  - SUPERSEDED. The V2 translations editor invalidates `["translations"]`
 *    when an admin saves (`useTranslations.ts`). The base entry has no
 *    observer of its own, so `refetchQueries` never reaches it — only
 *    treating "invalidated" as "must await" gets the edited text onto the
 *    page instead of five more minutes of the old wording.
 *  - MERELY AGED. Whole, nobody has superseded it, it is just older than
 *    `TRANSLATIONS_STALE_MS`. This one is served immediately and refreshed
 *    behind the user: the copy is correct, and making the switch wait on
 *    nine pages because of a clock is the latency this split exists to
 *    remove. Five minutes is short enough that someone reading a page and
 *    then changing language lands here routinely.
 *
 * The refreshed base reaches the screen on the language query's own next
 * refetch (its `staleTime`, mount, or window focus). That lag is acceptable
 * for ordinary ageing and is NOT acceptable for the two cases above, which
 * is the whole reason they are separated.
 *
 * `fetchQuery` dedupes by key, so two languages asking at once still make one
 * request, and a background refresh already in flight is not duplicated.
 */
export async function loadSharedBase(
  queryClient: QueryClient
): Promise<BaseDictionaries> {
  const state = queryClient.getQueryState<BaseDictionaries>(
    TRANSLATION_BASE_QUERY_KEY
  );
  const refetch = () =>
    queryClient.fetchQuery({
      queryKey: TRANSLATION_BASE_QUERY_KEY,
      queryFn: fetchBaseDictionaries,
      // Every caller below has already decided the cache cannot answer.
      staleTime: 0,
      gcTime: TRANSLATIONS_GC_MS,
    });

  /*
    A cache-state read cannot see a request that is ALREADY RUNNING, and
    `fetchQuery` joins one rather than starting its own. When a save lands
    mid-flight that is the SUPERSEDED case wearing a disguise: the in-flight
    request began before the save, and its success both clears
    `isInvalidated` and stamps `dataUpdatedAt: now` — so the pre-save wording
    ends up reading as whole, valid and fresh, which is the exact symptom the
    invalidated branch exists to prevent.

    So: let that request finish, then fetch AGAIN for rows that include the
    save. `refetch` passes `staleTime: 0`, so the second call really does go
    to the network rather than reading what the first one just stored.

    Two other routes were measured against query-core 5.102 and rejected.
    `refetchQueries({type: "all"})` looks right — it defaults to
    `cancelRefetch: true` — but on an entry with no observer it still settles
    on the pre-save value. `cancelQueries` does restart correctly, but the
    CancelledError propagates to whichever caller started the original
    request, turning someone else's successful load into a failure.
  */
  if (state?.isInvalidated && state.fetchStatus !== "idle") {
    // The first result is the pre-save one and is thrown away on purpose;
    // the catch only stops a rejected join from aborting the second fetch,
    // which is the one whose answer we want.
    await refetch().catch(() => undefined);
    return refetch();
  }

  const whole = state?.data?.complete === true && !state.isInvalidated;
  if (!whole || !state?.data) return refetch();

  // `fromStorage` counts as aged however recent the stamp is: react-query
  // dated it when the queryFn resolved, not when the data was last verified
  // against the table.
  const aged =
    state.data.fromStorage === true ||
    Date.now() - state.dataUpdatedAt >= TRANSLATIONS_STALE_MS;
  if (aged) {
    /*
      Fire and forget. The `catch` is deliberately DEFENSIVE, not
      load-bearing, and saying so matters because the two wrong readings of
      it fail in opposite directions: believe a failed fetch rejects here and
      you may thin out `fetchBaseDictionaries`'s try/catch as redundant;
      believe nothing can ever reject and you may delete this as dead code.

      What is true today, measured against query-core 5.102:
      `fetchBaseDictionaries` reports every failure by RESOLVING, and nothing
      in the app cancels this entry — `syncTranslations` and the translations
      editor both refetch ACTIVE queries, and this one has no observer — so
      no path currently rejects. It stays because an unhandled rejection on a
      promise nobody awaits would surface as a page error over a refresh the
      user never asked for, and that is one `cancelQueries` call away.
    */
    void refetch().catch(() => undefined);
  }
  return state.data;
}

function TranslationsBootstrapLoader() {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background"
      role="status"
      aria-live="polite"
    >
      <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
      <span className="sr-only">Loading</span>
    </div>
  );
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  // The provider sits inside BrowserRouter, so it can scope rendering to the
  // surface. Used only for `<html lang>` / `<html dir>` below.
  const { pathname } = useLocation();
  const [language, setLanguageState] = useState<Language>(() => {
    const raw =
      typeof window !== "undefined" ? localStorage.getItem("language") : null;
    return coerceLanguage(raw);
  });

  // Whether this browser holds a language the user actually picked. The effect
  // below writes localStorage on first render, so "was a choice ever made?"
  // has to be captured before that — otherwise every browser looks like it
  // already chose German and the account preference could never seed it.
  const hasExplicitChoiceRef = useRef(
    typeof window !== "undefined" &&
      isSupportedLanguage(localStorage.getItem("language"))
  );

  const setLanguage = useCallback((lang: Language) => {
    hasExplicitChoiceRef.current = true;
    setLanguageState(coerceLanguage(lang));
  }, []);

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  /**
   * What the CURRENT SURFACE renders in, as opposed to what the account chose.
   *
   * Only the tenant surfaces (`isTenantSurface`: the dashboard, the protocol
   * flow, deposit release) are translated into the four new languages, so
   * only they render the chosen language. Everything else — the admin
   * console, the owner portal, and the whole marketing site — renders German
   * or English and never mirrors: that is the protection COL-477 decision 6
   * was reaching for, scoped to the route rather than to the person so staff
   * and owners can hold a preference at all, and extended to the marketing
   * pages once a stored Ukrainian turned the public header into "Панель" on
   * an otherwise-English page (Neel, 6 Oct).
   *
   * `language` stays the account's actual choice: the pickers live on
   * /admin/account for owners, and they must show what is stored, not what
   * the surrounding page happens to render in.
   */
  const renderLanguage = rendersInBaseLanguage(pathname)
    ? baseLanguageFor(language)
    : language;

  // `<html lang>` was a static "en" although the app defaults to German;
  // screen readers and hyphenation pick the language up from here, and
  // Arabic flips the document direction (COL-478). `baseLanguageFor` never
  // returns an RTL language, so the portal is left-to-right by construction.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.lang = renderLanguage;
    root.dir = dirFor(renderLanguage);
  }, [renderLanguage]);

  // Seed from the account's saved preference — profiles.preferred_language,
  // the source of truth since COL-478 (Settings → Language and the first-login
  // picker write it) — so signing in on a new device starts in the right
  // language. Only when this browser has no explicit choice yet — a switch
  // made here must not be overridden by the account on every load — and only
  // when the account actually chose (language_chosen_at set): the column's
  // 'de' default is not a choice.
  //
  // Reads the session straight from supabase rather than useAuth: this
  // provider wraps AuthProvider, so the auth context does not exist yet here.
  useEffect(() => {
    let cancelled = false;
    // Bumped on every adopt() call and on SIGNED_OUT, so a response that
    // resolves after the browser has moved on — unmount, a newer adopt() call
    // for the SAME sign-in, or a sign-out/account-switch mid-flight — cannot
    // land late and overwrite the CURRENT account's language with a stale
    // one. Two overlapping requests can finish out of order (a slow request
    // for the account just left resolving after a fast one for the account
    // that replaced it); the sequence number is what lets the late one lose.
    let requestSeq = 0;

    const adopt = async (
      userId: string | undefined,
      metadata: Record<string, unknown> | null | undefined
    ) => {
      if (hasExplicitChoiceRef.current) return;
      // Bump only once we know there is a request to make. Bumping for a
      // signed-out callback would invalidate a real in-flight lookup that a
      // SIGNED_IN event started moments earlier, and the account's language
      // would silently not be adopted for that page load.
      if (!userId) return;
      const seq = ++requestSeq;

      const { data, error } = await supabase
        .from("profiles")
        .select("preferred_language, language_chosen_at")
        .eq("id", userId)
        .maybeSingle();

      if (cancelled || seq !== requestSeq || hasExplicitChoiceRef.current) {
        return;
      }

      if (error || !data || !data.language_chosen_at) {
        // Fall back to the auth metadata the pre-COL-478 code read, but ONLY
        // when the profiles read genuinely could not answer: the columns are
        // not deployed yet (a frontend release landing before its migration),
        // the account has no profile row, or the row came back unstamped
        // because this account last chose in Settings before COL-478.
        //
        // A transient network/server error is deliberately NOT one of those.
        // The metadata mirror that Settings writes is best-effort and can be
        // stale, so applying it on any failure could undo a newer explicit
        // choice — switch EN→DE, let the mirror fail, and one flaky read
        // would put the tenant back on English and persist it.
        const unanswerable = error
          ? isMissingColumn(error)
          : !data || !data.language_chosen_at;
        if (!unanswerable) return;

        const legacy = metadata?.preferred_language;
        if (legacy === "en" || legacy === "de") {
          setLanguageState(coerceLanguage(legacy));
        }
        return;
      }

      setLanguageState(coerceLanguage(data.preferred_language));
    };

    void supabase.auth.getSession().then(({ data }) => {
      void adopt(data.session?.user?.id, data.session?.user?.user_metadata);
    });

    // Sign-in usually happens after this provider mounted, so the initial read
    // above sees no session on the login flow. Token refreshes are not a
    // reason to re-read the profile.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        // Invalidate any adopt() still in flight for the account that just
        // left, so it cannot resolve after this and apply its language.
        requestSeq++;
        return;
      }
      if (
        event === "SIGNED_IN" ||
        event === "INITIAL_SESSION" ||
        event === "USER_UPDATED"
      ) {
        void adopt(session?.user?.id, session?.user?.user_metadata);
      }
    });

    return () => {
      cancelled = true;
      requestSeq++;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Fetch translations with React Query. Persisted localStorage payload
  // (if any) seeds initialData so the first paint isn't blank.
  const {
    data: translationData,
    isLoading,
    isPending,
  } = useQuery({
    // Keyed on the SURFACE's language, not the account's: an Arabic admin
    // reading the console gets the German/English dictionary, so none of the
    // 394 non-tenant keys that do carry Arabic leak into a screen whose
    // remaining copy is German.
    queryKey: ["translations", renderLanguage],
    // The en/de tiers come from one shared cache entry, so the six language
    // queries share a single copy and a single in-flight request instead of
    // each re-downloading the whole table. See `loadSharedBase`.
    queryFn: () =>
      fetchTranslations(renderLanguage, () => loadSharedBase(queryClient)),
    initialData: () => readPersistedTranslations(renderLanguage),
    initialDataUpdatedAt: () => readPersistedSavedAt(renderLanguage),
    /*
      Keep the previous language's dictionary on screen while the new one
      loads. Without this, switching language changes the query key, leaves
      `data` undefined for a device that has never fetched that language, and
      the provider below swaps the ENTIRE app for a full-screen spinner --
      unmounting the dashboard and, on the way back, refetching every other
      query on it. Showing the previous wording for a moment is a far smaller
      cost than blanking the page the tenant is reading.
    */
    placeholderData: keepPreviousData,
    /*
      Translations change when an admin edits them, which is rare. This used
      to be `staleTime: 0` with refetchOnMount AND refetchOnWindowFocus, so
      every mount and every tab focus re-read the whole table -- ~5,700 rows,
      not the "~300 small rows" the old comment claimed. Five minutes still
      lands an admin edit within a navigation or two while making a
      switch-back instant, and `syncTranslations()` below invalidates
      immediately for the admin who just made the edit.

      FUNCTION FORM, not a constant. `fetchTranslations` never throws -- a
      failed first page or a dropped parallel page still resolves with
      `fallbackOnly()` / a partial dictionary, so React Query stamps that
      result `dataUpdatedAt: now` regardless of completeness. A constant
      staleTime would then hold an incomplete dictionary authoritative for
      five minutes, and every `t()` without its own fallback renders its own
      raw key (`nav.dashboard` in the header, seen 14 Oct). Reading
      `query.state.data.complete` off the LIVE query closes the gap the
      persisted `savedAt` only covered for a fresh page load (Codex, 14 Oct).
    */
    staleTime: (query) =>
      query.state.data?.complete === false ? 0 : TRANSLATIONS_STALE_MS,
    gcTime: TRANSLATIONS_GC_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const cachedTranslations = translationData?.translations;
  const isTranslationsReady = translationData !== undefined;

  /**
   * @deprecated Shim for legacy Storyblok-sync callers. The new flow
   * is: admin edits via /admin/translations → DB row updates →
   * react-query invalidation → next read sees fresh values. Calling
   * this just forces an invalidate-and-refetch so the caller's
   * "after sync" expectations still hold.
   */
  const syncTranslations = async (_languageFilter?: Language) => {
    if (import.meta.env.DEV) {
      console.warn(
        "[i18n] syncTranslations() is deprecated. Translations are now edited in V2 admin (/admin/translations); the function only invalidates the local react-query cache."
      );
    }
    queryClient.invalidateQueries({ queryKey: ["translations"] });
    await queryClient.refetchQueries({ queryKey: ["translations"] });
  };

  const lookup = (
    key: string,
    fallback: string | undefined,
    fallbackBeatsOtherLanguages: boolean
  ): string => {
    // Prefer showing something rather than blank UI.
    if (!key) return "";

    if (translationData === undefined && isPending) {
      return fallback ?? "";
    }

    // English tier: rides along in the payload for fr/ar/uk/es; for German it
    // is whatever the English query left in the react-query cache (common
    // during rollout when a DE row is still missing).
    const enDict =
      translationData?.en ??
      (renderLanguage !== "en"
        ? queryClient.getQueryData<TranslationPayload>(["translations", "en"])
            ?.translations
        : undefined);

    const resolved = resolveTranslation(
      key,
      renderLanguage,
      { active: cachedTranslations, en: enDict, de: translationData?.de },
      fallback,
      { dev: import.meta.env.DEV, fallbackBeatsOtherLanguages }
    );

    if (
      import.meta.env.DEV &&
      (resolved.source === "fallback" || resolved.source === "none")
    ) {
      console.warn(
        `Translation key "${key}" not found for ${renderLanguage}. Add it in V2 admin → Translations.`
      );
    }
    return resolved.value;
  };

  const t = (key: string, fallback?: string): string =>
    lookup(key, fallback, false);

  const tDict = (
    key: string,
    literal: string,
    literalLanguage: SupportedLanguage
  ): string => lookup(key, literal, literalLanguage === renderLanguage);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        tDict,
        isLoading,
        isTranslationsReady,
        syncTranslations,
      }}
    >
      {isTranslationsReady ? children : <TranslationsBootstrapLoader />}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
